import {
  acceptDraftPrices,
  feedSaleStateFromAuctionStatus,
  fetchEdgeFunction,
  listingCopies,
  listingPriceLines,
  parseDollarsInput,
  withLiveFigures,
  type ListingPriceSettings,
  type PriceKey,
  type PublicLotRow,
} from '@auctisync/api-client';
import { Badge, Button, HelpTooltip } from '@auctisync/ui';
import { dollarsToCents, formatCents, type Tables, type TablesUpdate } from '@auctisync/shared';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AiCompleteModal } from '../../components/AiCompleteModal';
import { ListingPriceChoices } from '../../components/ListingPriceChoices';
import { LotDraftPanel } from '../../components/LotDraftPanel';
import { formFieldBlank, withAcceptedPrices, withSuggestion } from '../../lib/lotDraftForm';
import { BidderPreviewModal } from '../../components/BidderPreviewModal';
import { PhotoManager } from '../../components/PhotoManager';
import { PricingPredictionCard } from '../../components/PricingPredictionCard';
import { SalesCompStrip } from '../../components/SalesCompStrip';
import { useToast } from '../../components/Toast';
import { LiveStatusBanner } from '../../components/bidder-web/LiveStatusBanner';
import { useLiveSource } from '../../hooks/useLiveSource';
import { useLotSubscription } from '../../hooks/useLotSubscription';
import { useSaleFeed } from '../../hooks/useSaleFeed';
import { supabase } from '../../lib/supabase';

type Lot = Tables<'lots'>;
type ItemCategory = Pick<Tables<'item_categories'>, 'id' | 'name' | 'slug'>;
type Consignor = Pick<Tables<'consignors'>, 'id' | 'display_name'>;
type ProfileOption = { id: string; name: string; is_default: boolean };
type BidScale = Pick<Tables<'bid_increment_scales'>, 'id' | 'name' | 'is_default'>;

// 'closing' is a derived urgency phase (from the countdown), not a hand-set
// status — omitted from the selectable options.
const LOT_STATUSES = [
  'draft',
  'scheduled',
  'active',
  'sold',
  'unsold',
  'no_sale',
  'cancelled',
] as const;

type FormState = {
  lot_number: string;
  sale_order: string;
  control_number: string;
  title: string;
  description: string;
  condition: string;
  quantity: string;
  category_id: string;
  consignor_id: string;
  consignment_profile_id: string;
  // Pricing
  retail_price: string;
  msrp: string;
  starting_bid: string;
  reserve: string;
  bid_increment_scale_id: string;
  estimate_min: string;
  estimate_max: string;
  // Identifiers
  mpn: string;
  upc: string;
  sku: string;
  // Specs
  custom_fields: Record<string, unknown>;
  // Location
  location_bin: string;
  location_notes: string;
  // Which prices bidders see — part of the form, saved with everything else.
  show_msrp: boolean;
  show_retail_price: boolean;
  show_estimate: boolean;
  // Status + visibility
  status: string;
  is_visible: boolean;
  tax_exempt: boolean;
  is_featured: boolean;
  exclude_from_platform_pool: boolean;
};

function lotToForm(l: Lot): FormState {
  const centsToDollars = (c: number | null) => (c == null ? '' : (c / 100).toFixed(2));
  return {
    lot_number: String(l.lot_number),
    sale_order: l.sale_order === null ? '' : String(l.sale_order),
    control_number: l.control_number ?? '',
    title: l.title ?? '',
    description: l.description ?? '',
    condition: l.condition ?? '',
    quantity: String(l.quantity),
    category_id: l.category_id ?? '',
    consignor_id: l.consignor_id ?? '',
    consignment_profile_id: l.consignment_profile_id ?? '',
    retail_price: centsToDollars(l.retail_price_cents),
    msrp: centsToDollars(l.msrp_cents),
    starting_bid: centsToDollars(l.starting_bid_cents),
    reserve: centsToDollars(l.reserve_cents),
    bid_increment_scale_id: l.bid_increment_scale_id ?? '',
    estimate_min: centsToDollars(l.estimate_min_cents),
    estimate_max: centsToDollars(l.estimate_max_cents),
    mpn: l.mpn ?? '',
    upc: l.upc ?? '',
    sku: l.sku ?? '',
    custom_fields:
      l.custom_fields && typeof l.custom_fields === 'object' && !Array.isArray(l.custom_fields)
        ? (l.custom_fields as Record<string, unknown>)
        : {},
    location_bin: l.location_bin ?? '',
    location_notes: l.location_notes ?? '',
    show_msrp: l.show_msrp,
    show_retail_price: l.show_retail_price,
    show_estimate: l.show_estimate,
    status: l.status,
    is_visible: l.is_visible,
    tax_exempt: l.tax_exempt,
    is_featured: l.is_featured,
    exclude_from_platform_pool: l.exclude_from_platform_pool,
  };
}

function formToUpdate(form: FormState): TablesUpdate<'lots'> {
  const s = (v: string) => (v.trim() === '' ? null : v.trim());
  const d = (v: string) => (v.trim() === '' ? null : dollarsToCents(Number(v)));
  const startingBid = d(form.starting_bid);
  return {
    lot_number: parseInt(form.lot_number || '0', 10) || 1,
    sale_order: form.sale_order.trim() ? parseInt(form.sale_order.trim(), 10) : null,
    control_number: s(form.control_number),
    title: s(form.title),
    description: s(form.description),
    condition: s(form.condition),
    quantity: Math.max(1, parseInt(form.quantity || '1', 10) || 1),
    category_id: s(form.category_id),
    consignor_id: s(form.consignor_id),
    consignment_profile_id: s(form.consignment_profile_id),
    retail_price_cents: d(form.retail_price),
    msrp_cents: d(form.msrp),
    starting_bid_cents: startingBid ?? 100,
    reserve_cents: d(form.reserve),
    bid_increment_scale_id: s(form.bid_increment_scale_id),
    estimate_min_cents: d(form.estimate_min),
    estimate_max_cents: d(form.estimate_max),
    mpn: s(form.mpn),
    upc: s(form.upc),
    sku: s(form.sku),
    custom_fields: (form.custom_fields ?? {}) as NonNullable<TablesUpdate<'lots'>['custom_fields']>,
    location_bin: s(form.location_bin),
    location_notes: s(form.location_notes),
    show_msrp: form.show_msrp,
    show_retail_price: form.show_retail_price,
    show_estimate: form.show_estimate,
    status: form.status,
    is_visible: form.is_visible,
    tax_exempt: form.tax_exempt,
    is_featured: form.is_featured,
    exclude_from_platform_pool: form.exclude_from_platform_pool,
  };
}

/**
 * The lot form's id: the Save at the TOP of the page sits outside the form
 * (in the header), and submits it by name (Jim, 2026-10-06 — the form is long
 * and the only Save was at the bottom).
 */
const LOT_FORM_ID = 'lot-form';

/** A price's field in the lot form — the shared keys, spelled for this form. */
const PRICE_FORM_KEY: Record<
  PriceKey,
  'msrp' | 'retail_price' | 'starting_bid' | 'reserve' | 'estimate_min' | 'estimate_max'
> = {
  msrp_cents: 'msrp',
  retail_price_cents: 'retail_price',
  starting_bid_cents: 'starting_bid',
  reserve_cents: 'reserve',
  estimate_min_cents: 'estimate_min',
  estimate_max_cents: 'estimate_max',
};

/**
 * The lot's "Show on listing" choices as the OPEN FORM holds them — not as the
 * lot was loaded. Every surface that shows them (the panel, the Pricing
 * section, Preview as bidder) reads this, so an unsaved tick shows everywhere
 * at once and the preview tells the truth about what Save would do.
 */
function formListing(form: FormState): ListingPriceSettings {
  return {
    msrp: form.show_msrp,
    retail_price: form.show_retail_price,
    estimate: form.show_estimate,
  };
}

export function AdminLotDetail() {
  const { id: auctionId, lotId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [lot, setLot] = useState<Lot | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [consignors, setConsignors] = useState<Consignor[]>([]);
  const [consignProfiles, setConsignProfiles] = useState<ProfileOption[]>([]);
  const [scales, setScales] = useState<BidScale[]>([]);

  // Set by the cataloguing panel: this lot's AI prices still want a person.
  const [pricesPending, setPricesPending] = useState(false);
  const [showAiComplete, setShowAiComplete] = useState(false);
  const [showBidderPreview, setShowBidderPreview] = useState(false);
  const [msrpBusy, setMsrpBusy] = useState(false);
  const [msrpMsg, setMsrpMsg] = useState<string | null>(null);
  const [pullBusy, setPullBusy] = useState(false);

  async function handlePullFromItem() {
    if (!lot || !lotId) return;
    if (
      !window.confirm(
        'Replace this lot\u2019s fields with the inventory item\u2019s current values? Any edits you\u2019ve made on this lot will be overwritten. Photos are not affected.',
      )
    ) {
      return;
    }
    setPullBusy(true);
    setError(null);
    const { data, error: e } = await supabase.rpc('pull_lot_from_item', {
      p_lot_id: lotId,
    });
    setPullBusy(false);
    if (e) {
      setError(e.message);
      return;
    }
    if (data) {
      setLot(data);
      setForm(lotToForm(data));
      toast.success('Lot refreshed from inventory.');
    }
  }
  const [photos, setPhotos] = useState<Array<{ display_url: string; caption: string | null }>>([]);

  // Fetch lot-owned photos for the Bidder Preview modal.
  useEffect(() => {
    if (!lotId) return;
    void (async () => {
      const { data } = await supabase
        .from('inventory_photos')
        .select('display_url, caption')
        .eq('lot_id', lotId)
        .order('sort_order', { ascending: true });
      setPhotos(data ?? []);
    })();
  }, [lotId]);

  useEffect(() => {
    void (async () => {
      const [{ data: cats }, { data: cons }, { data: scs }, { data: profs }] = await Promise.all([
        supabase
          .from('item_categories')
          .select('id, name, slug')
          .is('archived_at', null)
          .order('name', { ascending: true }),
        supabase
          .from('consignors')
          .select('id, display_name')
          .eq('status', 'active')
          .order('display_name', { ascending: true }),
        supabase
          .from('bid_increment_scales')
          .select('id, name, is_default')
          .is('archived_at', null)
          .order('is_default', { ascending: false })
          .order('name', { ascending: true }),
        supabase
          .from('consignment_profiles')
          .select('id, name, is_default')
          .is('archived_at', null)
          .order('is_default', { ascending: false })
          .order('name', { ascending: true }),
      ]);
      setCategories(cats ?? []);
      setConsignors(cons ?? []);
      setScales(scs ?? []);
      setConsignProfiles(profs ?? []);
    })();
  }, []);

  // When the form was last filled from the server. The cataloguing draft panel
  // compares it with the draft's arrival to warn that a Save would clear fields
  // the AI filled after the page opened.
  const [loadedAt, setLoadedAt] = useState(() => new Date().toISOString());
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!lotId) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      // lots_staff: the whole lot, for the lot's own house. `select('*')` on
      // public.lots fails for the shared authenticated role since the house's
      // numbers were hidden from it (20260923210000). Writes still go to lots.
      const { data, error: err } = await supabase
        .from('lots_staff')
        .select('*')
        .eq('id', lotId)
        .maybeSingle();
      if (cancelled) return;
      if (err) setError(err.message);
      else if (!data) setError('Lot not found.');
      else {
        setLot(data);
        setForm(lotToForm(data));
        setLoadedAt(new Date().toISOString());
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [lotId, reloadKey]);

  /**
   * 🔴 THE LOT'S LIVE FIGURES (2026-10-08): the house's view of a lot moves to
   * the engine with everything else. On a sale the engine runs, a staff feed
   * on this lot keeps its state, bid count and current bid live in the header
   * — and the "has received bids" guards honest — without a reload. A sale on
   * the Supabase book keeps Realtime. The form is the catalogue's and is not
   * touched: a live figure never lands in a field someone is editing.
   */
  const [sale, setSale] = useState<Pick<Tables<'auctions'>, 'status' | 'timezone'> | null>(null);
  useEffect(() => {
    if (!auctionId) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('auctions')
        .select('status, timezone')
        .eq('id', auctionId)
        .maybeSingle();
      if (!cancelled) setSale(data ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [auctionId]);
  const { source } = useLiveSource(lot === null ? null : lot.id);
  const live = useSaleFeed(source, {
    view: lot === null ? [] : [lot.id],
    initialSaleState: feedSaleStateFromAuctionStatus(sale?.status),
  });
  const onRealtimeRow = useCallback((row: PublicLotRow) => {
    setLot((prev) =>
      prev !== null && prev.id === row.id
        ? {
            ...prev,
            current_bid_cents: row.current_bid_cents,
            bid_count: row.bid_count,
            ends_at: row.ends_at,
            status: row.status,
          }
        : prev,
    );
  }, []);
  useLotSubscription({
    target: { kind: 'lot', lotId: lot?.id ?? '' },
    onUpdate: onRealtimeRow,
    enabled: source?.kind === 'supabase' && lot !== null,
  });
  const liveLot = lot === null ? null : withLiveFigures(lot, live.feed?.lot(lot.id));
  /** Bids on the lot now — the guards below read it, never a count loaded minutes ago. */
  const liveBidCount = liveLot?.bid_count ?? 0;

  /**
   * The AI's prices, offered once its draft comes up for review: into the
   * FORM, where the person sees them beside what the AI suggested, edits them
   * and saves them with everything else. The form's price keys ARE the shared
   * field keys. Until 2026-10-06 a button of the panel's own wrote them
   * straight to the lot, so a person had to press it AND Save.
   */
  const proposePrices = useCallback((prices: Partial<Record<PriceKey, number>>) => {
    setForm((f) => (f ? withAcceptedPrices(f, prices) : f));
  }, []);

  /** "Show on listing" is part of the form now — Save writes it with the rest. */
  const changeListing = useCallback((patch: Partial<ListingPriceSettings>) => {
    setForm((f) =>
      f
        ? {
            ...f,
            ...(patch.msrp === undefined ? {} : { show_msrp: patch.msrp }),
            ...(patch.retail_price === undefined ? {} : { show_retail_price: patch.retail_price }),
            ...(patch.estimate === undefined ? {} : { show_estimate: patch.estimate }),
          }
        : f,
    );
  }, []);

  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }, []);

  /**
   * One Save for the page (Jim, 2026-10-06).
   *
   * When a cataloguing draft is still waiting on its prices, the prices in the
   * form go through `acceptDraftPrices` FIRST — the one door that checks the
   * caller is staff, refuses a live lot, refuses a reserve under the starting
   * bid or a low estimate over the high one, and stamps the draft reviewed so
   * it stops asking. Only then does the rest of the form save. A refusal stops
   * the save with the server's words, rather than writing half a lot.
   */
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form || !lotId) return;
    setError(null);
    setSaving(true);
    if (pricesPending) {
      const overrides: Partial<Record<PriceKey, number>> = {};
      for (const [price, raw] of [
        ['msrp_cents', form.msrp],
        ['retail_price_cents', form.retail_price],
        ['starting_bid_cents', form.starting_bid],
        ['reserve_cents', form.reserve],
        ['estimate_min_cents', form.estimate_min],
        ['estimate_max_cents', form.estimate_max],
      ] as [PriceKey, string][]) {
        const cents = parseDollarsInput(raw);
        if (cents === undefined) {
          setSaving(false);
          setError(`Enter an amount like 25 or 25.50 for every price.`);
          return;
        }
        if (cents !== null) overrides[price] = cents;
      }
      const res = await acceptDraftPrices(supabase, lotId, overrides);
      if (!res.ok) {
        setSaving(false);
        setError(res.message);
        toast.error('Changes not saved.');
        return;
      }
    }
    const patch = formToUpdate(form);
    const { error: err } = await supabase.from('lots').update(patch).eq('id', lotId);
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    toast.success('Changes saved.');
    // The draft has been stamped reviewed; let the panel show its new stage.
    if (pricesPending) setReloadKey((k) => k + 1);
  }

  async function handleRemove() {
    if (!lot || !lotId) return;
    if (liveBidCount > 0) {
      window.alert('Cannot remove a lot that has received bids. Cancel it via the status field.');
      return;
    }
    if (
      !window.confirm(
        'Remove this lot from the auction? The inventory item is not deleted and will be available again.',
      )
    ) {
      return;
    }
    setSaving(true);
    const { error: err } = await supabase.from('lots').delete().eq('id', lotId);
    setSaving(false);
    if (err) setError(err.message);
    else void navigate(`/admin/auctions/${auctionId}`);
  }

  if (loading || !form) {
    return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Loading lot…</div>;
  }

  if (!lot) {
    return (
      <div style={{ padding: '2rem', color: 'var(--text-danger, #991b1b)' }}>
        {error ?? 'Lot not found.'}
      </div>
    );
  }

  const shown = liveLot ?? lot;

  return (
    <div>
      <LiveStatusBanner status={live.status} timeZone={sale?.timezone} audience="staff" />
      <header style={{ marginBottom: '1.5rem' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <Link to={`/admin/auctions/${auctionId}`} style={backLinkStyle}>
            ← Auction
          </Link>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div
              style={{
                fontFamily: 'var(--font-heading-active)',
                fontWeight: 600,
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--brand-accent)',
                marginBottom: '0.5rem',
              }}
            >
              Lot ·{' '}
              {shown.status === 'scheduled'
                ? 'Scheduled'
                : shown.status === 'active' || shown.status === 'closing'
                  ? 'Live'
                  : shown.status === 'sold'
                    ? 'Sold'
                    : shown.status.charAt(0).toUpperCase() + shown.status.slice(1)}
            </div>
            <h1
              style={{
                fontFamily: 'var(--font-heading-active)',
                fontSize: 'clamp(1.6rem, 1.2rem + 1.4vw, 2rem)',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: 'var(--text)',
                marginBottom: '0.25rem',
              }}
            >
              Lot #{lot.lot_number}
              {form.title ? ` — ${form.title}` : ''}
            </h1>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <Badge variant={shown.status === 'active' ? 'success' : 'outline'}>
                {shown.status}
              </Badge>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                {liveBidCount} bid{liveBidCount === 1 ? '' : 's'}
                {shown.current_bid_cents !== null && (
                  <>
                    {' '}
                    · current <strong>{formatCents(shown.current_bid_cents)}</strong>
                  </>
                )}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {lot.inventory_item_id && (
              <Button
                type="button"
                variant="outline"
                disabled={pullBusy}
                onClick={() => {
                  void handlePullFromItem();
                }}
                title="Overwrite this lot's fields with the inventory item's current values. Photos are not affected."
              >
                {pullBusy ? 'Pulling…' : '↻ Pull latest from inventory'}
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => setShowBidderPreview(true)}>
              👁 Preview as bidder
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAiComplete(true)}
              disabled={liveBidCount > 0}
              title={
                liveBidCount > 0 ? 'AI Complete disabled once bidding starts' : 'Run AI Complete'
              }
            >
              ⚡ Run AI Complete
            </Button>
            {/* Jim, 2026-10-06: a Save at the top as well — the form is long. */}
            <Button type="submit" form={LOT_FORM_ID} disabled={saving} data-testid="lot-save-top">
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      </header>

      {error && <AlertBox variant="danger">{error}</AlertBox>}

      <LotDraftPanel
        lotId={lot.id}
        lotStatus={lot.status}
        loadedAt={loadedAt}
        onPricesProposed={proposePrices}
        onPricesPending={setPricesPending}
        priceValue={(price) => form[PRICE_FORM_KEY[price]]}
        onPriceChange={(price, value) => {
          setField(PRICE_FORM_KEY[price], value);
        }}
        onReload={() => {
          setReloadKey((k) => k + 1);
        }}
        categories={categories}
        isBlank={(field) => formFieldBlank(form, field)}
        onUseSuggestion={(patch) => {
          setForm((f) => (f ? withSuggestion(f, patch) : f));
        }}
        listing={formListing(form)}
        onListingChange={changeListing}
      />

      <form
        id={LOT_FORM_ID}
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        style={{ display: 'grid', gap: '1.5rem' }}
      >
        <Section title="Identity">
          <TwoCol>
            <Field label="Lot number" required>
              <input
                type="number"
                min={1}
                value={form.lot_number}
                onChange={(e) => setField('lot_number', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Sale order">
              <input
                type="number"
                value={form.sale_order}
                onChange={(e) => setField('sale_order', e.target.value)}
                style={inputStyle}
              />
            </Field>
          </TwoCol>
          {lot.inventory_item_id && (
            <div
              style={{
                fontSize: '0.8rem',
                color: 'var(--text-muted)',
                background: 'var(--surface-muted)',
                padding: '0.5rem 0.75rem',
                borderRadius: 'var(--r-tile, 0.375rem)',
              }}
            >
              Originally seeded from{' '}
              <Link
                to={`/admin/inventory/${lot.inventory_item_id}`}
                style={{ color: 'var(--brand-accent)' }}
              >
                inventory item
              </Link>
              . Edits here affect THIS lot only — they never write back to the inventory record or
              touch other auctions the item appears in.
            </div>
          )}
        </Section>

        <Section title="Content">
          <Field label="Title">
            <input
              type="text"
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="Description">
            <textarea
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              rows={4}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '5rem' }}
            />
          </Field>
          <TwoCol>
            <Field label="Condition">
              <input
                type="text"
                value={form.condition}
                onChange={(e) => setField('condition', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field
              label="Quantity"
              help={
                <HelpTooltip helpSlug="adding-lots-from-inventory">
                  How many units of the item this lot sells. Case lots use &gt; 1.
                </HelpTooltip>
              }
            >
              <input
                type="number"
                min={1}
                value={form.quantity}
                onChange={(e) => setField('quantity', e.target.value)}
                style={inputStyle}
              />
            </Field>
          </TwoCol>
        </Section>

        <Section title="Photos">
          <p
            style={{
              fontSize: '0.85rem',
              color: 'var(--text-muted)',
              margin: '0 0 0.75rem 0',
            }}
          >
            Photos uploaded here live with this specific lot. Edits here never affect the underlying
            inventory item or any other auction the item appears in.
          </p>
          <PhotoManager lotId={lot.id} tenantId={lot.tenant_id} />
        </Section>

        <Section title="Identifiers">
          <TwoCol>
            <Field label="Control number">
              <input
                type="text"
                value={form.control_number}
                onChange={(e) => setField('control_number', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="MPN">
              <input
                type="text"
                value={form.mpn}
                onChange={(e) => setField('mpn', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="UPC">
              <input
                type="text"
                value={form.upc}
                onChange={(e) => setField('upc', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="SKU">
              <input
                type="text"
                value={form.sku}
                onChange={(e) => setField('sku', e.target.value)}
                style={inputStyle}
              />
            </Field>
          </TwoCol>
        </Section>

        {Object.keys(form.custom_fields).length > 0 && (
          <Section title="Specs">
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 0.5rem' }}>
              Structured fields shown to bidders under Specifications on the lot page. Populated by
              AI Complete — re-run it to refresh.
            </p>
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'max-content minmax(0, 1fr)',
                overflowWrap: 'anywhere',
                columnGap: '1rem',
                rowGap: '0.3rem',
                margin: 0,
                fontSize: '0.9rem',
              }}
            >
              {Object.entries(form.custom_fields).map(([k, v]) => {
                const display =
                  typeof v === 'string'
                    ? v
                    : typeof v === 'number'
                      ? String(v)
                      : typeof v === 'boolean'
                        ? v
                          ? 'yes'
                          : 'no'
                        : '';
                if (!display) return null;
                return (
                  <div key={k} style={{ display: 'contents' }}>
                    <dt style={{ color: 'var(--text-muted)', textTransform: 'capitalize' }}>
                      {k.replace(/_/g, ' ')}
                    </dt>
                    <dd style={{ margin: 0, color: 'var(--text)' }}>{display}</dd>
                  </div>
                );
              })}
            </dl>
          </Section>
        )}

        <Section title="Classification">
          <TwoCol>
            <Field label="Category">
              <select
                value={form.category_id}
                onChange={(e) => setField('category_id', e.target.value)}
                style={inputStyle}
              >
                <option value="">— None —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Consignor">
              <select
                value={form.consignor_id}
                onChange={(e) => setField('consignor_id', e.target.value)}
                style={inputStyle}
              >
                <option value="">— None / house —</option>
                {consignors.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.display_name}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Consignment terms"
              help={
                <HelpTooltip helpSlug="consignment-profiles">
                  Override the commission + fees for this specific lot at settlement. Leave on
                  inherit to use the consignor&apos;s, then the auction&apos;s, then the tenant
                  default.
                </HelpTooltip>
              }
            >
              <select
                value={form.consignment_profile_id}
                onChange={(e) => setField('consignment_profile_id', e.target.value)}
                style={inputStyle}
              >
                <option value="">— Inherit (consignor / auction / default) —</option>
                {consignProfiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.is_default ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </Field>
          </TwoCol>
        </Section>

        <Section title="Pricing">
          <div style={{ marginBottom: '1rem' }}>
            <SalesCompStrip
              tenantId={lot.tenant_id}
              title={form.title}
              mpn={form.mpn || null}
              categoryId={form.category_id || null}
              categoryName={categories.find((c) => c.id === form.category_id)?.name || null}
              condition={form.condition || null}
            />
          </div>
          <TwoCol>
            <Field label="Retail price">
              <DollarInput
                value={form.retail_price}
                onChange={(v) => setField('retail_price', v)}
              />
            </Field>
            <Field
              label="MSRP"
              help={
                <HelpTooltip helpSlug="ai-auto-description">
                  Manufacturer Suggested Retail Price. Click Research to let AuctiSync AI look it up
                  from the web.
                </HelpTooltip>
              }
            >
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <DollarInput value={form.msrp} onChange={(v) => setField('msrp', v)} />
                <Button
                  type="button"
                  variant="outline"
                  disabled={msrpBusy}
                  onClick={() => {
                    void (async () => {
                      setMsrpBusy(true);
                      setMsrpMsg(null);
                      try {
                        // Direct so the error body stays readable; token and the
                        // one-refresh retry on a 401 come from the shared client.
                        const res = await fetchEdgeFunction(supabase, 'research-item-msrp', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ lot_id: lot.id }),
                        });
                        const raw = await res.text();
                        let data: {
                          msrp_cents?: number | null;
                          error?: string;
                          detail?: string;
                        } = {};
                        try {
                          data = JSON.parse(raw) as typeof data;
                        } catch {
                          data = { error: raw.slice(0, 300) };
                        }
                        if (!res.ok || data.error) {
                          setMsrpMsg(
                            [`HTTP ${res.status}`, data.error, data.detail]
                              .filter(Boolean)
                              .join(' · '),
                          );
                        } else if (data.msrp_cents != null) {
                          setField('msrp', (data.msrp_cents / 100).toFixed(2));
                          setMsrpMsg(`AuctiSync AI suggested ${formatCents(data.msrp_cents)}`);
                        } else {
                          setMsrpMsg('AuctiSync AI could not find a reliable MSRP.');
                        }
                      } catch (e) {
                        setMsrpMsg(e instanceof Error ? e.message : 'Request failed');
                      } finally {
                        setMsrpBusy(false);
                      }
                    })();
                  }}
                >
                  {msrpBusy ? 'Researching…' : 'Research'}
                </Button>
              </div>
              {msrpMsg && (
                <div
                  style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}
                >
                  {msrpMsg}
                </div>
              )}
            </Field>
            <Field label="Starting bid" required>
              <DollarInput
                value={form.starting_bid}
                onChange={(v) => setField('starting_bid', v)}
              />
            </Field>
            <Field label="Reserve (optional)">
              <DollarInput value={form.reserve} onChange={(v) => setField('reserve', v)} />
            </Field>
            <Field label="Estimate (low)">
              <DollarInput
                value={form.estimate_min}
                onChange={(v) => setField('estimate_min', v)}
              />
            </Field>
            <Field label="Estimate (high)">
              <DollarInput
                value={form.estimate_max}
                onChange={(v) => setField('estimate_max', v)}
              />
            </Field>
            <Field label="Bid increment scale">
              <select
                value={form.bid_increment_scale_id}
                onChange={(e) => setField('bid_increment_scale_id', e.target.value)}
                style={inputStyle}
              >
                <option value="">— Auction default —</option>
                {scales.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.is_default ? ' (tenant default)' : ''}
                  </option>
                ))}
              </select>
            </Field>
          </TwoCol>
          <ListingPriceChoices value={formListing(form)} onChange={changeListing} />
        </Section>

        <Section title="AuctiSync AI">
          <PricingPredictionCard lotId={lot.id} />
        </Section>

        <Section title="Location">
          <TwoCol>
            <Field label="Bin / shelf">
              <input
                type="text"
                value={form.location_bin}
                onChange={(e) => setField('location_bin', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Notes">
              <input
                type="text"
                value={form.location_notes}
                onChange={(e) => setField('location_notes', e.target.value)}
                style={inputStyle}
              />
            </Field>
          </TwoCol>
        </Section>

        <Section title="Status & Visibility">
          <TwoCol>
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
                style={inputStyle}
              >
                {LOT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
          </TwoCol>
          <div style={{ display: 'grid', gap: '0.4rem', marginTop: '0.5rem' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem',
                color: 'var(--text)',
              }}
            >
              <input
                type="checkbox"
                checked={form.is_visible}
                onChange={(e) => setField('is_visible', e.target.checked)}
              />
              Visible to bidders
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem',
                color: 'var(--text)',
              }}
            >
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => setField('is_featured', e.target.checked)}
              />
              Feature this lot on the auction page
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem',
                color: 'var(--text)',
              }}
            >
              <input
                type="checkbox"
                checked={form.tax_exempt}
                onChange={(e) => setField('tax_exempt', e.target.checked)}
              />
              Tax exempt (this lot is never taxed)
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                fontSize: '0.9rem',
                color: 'var(--text)',
              }}
            >
              <input
                type="checkbox"
                checked={form.exclude_from_platform_pool}
                onChange={(e) => setField('exclude_from_platform_pool', e.target.checked)}
                style={{ marginTop: '0.2rem' }}
              />
              <span>
                Exclude from the platform comp pool
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  This lot still counts in your own Sales Intel. Use this for heritage or
                  identifying lots that shouldn&apos;t contribute to the cross-tenant benchmark.
                </div>
              </span>
            </label>
          </div>
        </Section>

        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingTop: '0.5rem',
            borderTop: '1px solid var(--admin-hairline, var(--border))',
            flexWrap: 'wrap',
          }}
        >
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              void handleRemove();
            }}
            disabled={saving || liveBidCount > 0}
            style={{ marginRight: 'auto' }}
          >
            Remove lot
          </Button>
          {error && (
            <span
              role="alert"
              style={{
                color: 'var(--danger, #b91c1c)',
                fontSize: '0.9rem',
                fontWeight: 500,
              }}
            >
              {error}
            </span>
          )}
          <Button type="button" variant="outline" asChild>
            <Link to={`/admin/auctions/${auctionId}`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={saving} data-testid="lot-save-bottom">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>

      {showAiComplete && (
        <AiCompleteModal
          lotId={lot.id}
          enabledFields={{
            title: true,
            description: true,
            condition: true,
            category_slug: true,
            mpn: true,
            upc: true,
            msrp_cents: true,
            retail_price_cents: true,
            starting_bid_cents: true,
            reserve_cents: true,
            estimate_min_cents: true,
            estimate_max_cents: true,
            custom_fields: true,
          }}
          currentItem={{
            title: form.title || null,
            description: form.description || null,
            condition: form.condition || null,
            category_id: form.category_id || null,
            mpn: form.mpn || null,
            upc: form.upc || null,
            msrp_cents: form.msrp ? Math.round(Number(form.msrp) * 100) : null,
            retail_price_cents: form.retail_price
              ? Math.round(Number(form.retail_price) * 100)
              : null,
            starting_bid_cents: form.starting_bid
              ? Math.round(Number(form.starting_bid) * 100)
              : null,
            reserve_cents: form.reserve ? Math.round(Number(form.reserve) * 100) : null,
            estimate_min_cents: form.estimate_min
              ? Math.round(Number(form.estimate_min) * 100)
              : null,
            estimate_max_cents: form.estimate_max
              ? Math.round(Number(form.estimate_max) * 100)
              : null,
            custom_fields: form.custom_fields,
          }}
          onApply={(payload) => {
            const centsToDollars = (c: number | null | undefined) =>
              c == null ? undefined : (c / 100).toFixed(2);
            setForm((f) => {
              if (!f) return f;
              const next = { ...f };
              if (payload.title !== undefined) next.title = payload.title ?? '';
              if (payload.description !== undefined) next.description = payload.description ?? '';
              if (payload.condition !== undefined) next.condition = payload.condition ?? '';
              if (payload.mpn !== undefined) next.mpn = payload.mpn ?? '';
              if (payload.upc !== undefined) next.upc = payload.upc ?? '';
              const msrpD = centsToDollars(payload.msrp_cents);
              if (msrpD !== undefined) next.msrp = msrpD;
              const retailD = centsToDollars(payload.retail_price_cents);
              if (retailD !== undefined) next.retail_price = retailD;
              const startD = centsToDollars(payload.starting_bid_cents);
              if (startD !== undefined) next.starting_bid = startD;
              const reserveD = centsToDollars(payload.reserve_cents);
              if (reserveD !== undefined) next.reserve = reserveD;
              const emnD = centsToDollars(payload.estimate_min_cents);
              if (emnD !== undefined) next.estimate_min = emnD;
              const emxD = centsToDollars(payload.estimate_max_cents);
              if (emxD !== undefined) next.estimate_max = emxD;
              if (Object.keys(payload.customFields).length > 0) {
                next.custom_fields = {
                  ...(next.custom_fields ?? {}),
                  ...payload.customFields,
                };
              }
              if (payload.category_slug) {
                const match = categories.find((c) => c.slug === payload.category_slug);
                if (match) next.category_id = match.id;
              }
              return next;
            });
            setShowAiComplete(false);
          }}
          onClose={() => setShowAiComplete(false)}
        />
      )}

      {showBidderPreview && (
        <BidderPreviewModal
          photos={photos}
          title={form.title}
          condition={form.condition}
          startingBidCents={form.starting_bid ? Math.round(Number(form.starting_bid) * 100) : null}
          reserveCents={form.reserve ? Math.round(Number(form.reserve) * 100) : null}
          description={form.description}
          specs={form.custom_fields}
          prices={listingPriceLines(
            listingCopies(
              {
                msrp_cents: form.msrp ? Math.round(Number(form.msrp) * 100) : null,
                retail_price_cents: form.retail_price
                  ? Math.round(Number(form.retail_price) * 100)
                  : null,
                estimate_min_cents: form.estimate_min
                  ? Math.round(Number(form.estimate_min) * 100)
                  : null,
                estimate_max_cents: form.estimate_max
                  ? Math.round(Number(form.estimate_max) * 100)
                  : null,
              },
              formListing(form),
            ),
          )}
          onClose={() => setShowBidderPreview(false)}
        />
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section
      style={{
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-card, 0.5rem)',
        background: 'var(--surface-elev)',
        boxShadow: 'var(--elev-1)',
        padding: '1.1rem 1.25rem',
      }}
    >
      <h2
        style={{
          fontFamily: 'var(--font-heading-active)',
          fontSize: '0.8rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--text-muted)',
          marginBottom: '0.85rem',
        }}
      >
        {title}
      </h2>
      <div style={{ display: 'grid', gap: '0.75rem' }}>{children}</div>
    </section>
  );
}

function TwoCol({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gap: '0.75rem',
        gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))',
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  help,
  required,
  children,
}: {
  label: string;
  help?: ReactNode;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'grid', gap: '0.35rem' }}>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.35rem',
          fontSize: '0.8rem',
          fontWeight: 500,
          color: 'var(--text)',
        }}
      >
        {label}
        {required && <span style={{ color: 'var(--semantic-error, #b91c1c)' }}>*</span>}
        {help}
      </span>
      {children}
    </label>
  );
}

function DollarInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ position: 'relative' }}>
      <span
        style={{
          position: 'absolute',
          left: '0.75rem',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--text-muted)',
          fontSize: '0.875rem',
        }}
      >
        $
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, paddingLeft: '1.5rem' }}
        placeholder="0.00"
      />
    </div>
  );
}

function AlertBox({ variant, children }: { variant: 'danger' | 'success'; children: ReactNode }) {
  const palette =
    variant === 'danger'
      ? {
          bg: 'var(--surface-danger, #fef2f2)',
          fg: 'var(--text-danger, #991b1b)',
          border: 'var(--border-danger, #fecaca)',
        }
      : {
          bg: 'var(--surface-success, #f0fdf4)',
          fg: 'var(--text-success, #166534)',
          border: 'var(--border-success, #bbf7d0)',
        };
  return (
    <div
      role={variant === 'danger' ? 'alert' : 'status'}
      style={{
        padding: '0.75rem 1rem',
        borderRadius: 'var(--r-tile, 0.375rem)',
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        marginBottom: '1rem',
        fontSize: '0.875rem',
      }}
    >
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
};

const backLinkStyle: React.CSSProperties = {
  color: 'var(--brand-accent)',
  fontSize: '0.875rem',
  textDecoration: 'none',
};
